import range from 'lodash/range';
import { computed, ComputedRef, ref, Ref } from 'vue';
import ProductFilterSlider from '@dh-io-emh/shared/src/models/product-filter/product-filter-slider.model';
import { DropdownRangeOption } from '@dh-io-emh/ui/src/form/dropdown/dropdownRange/dropdown-range.model';
import { roundToStep, roundToStepLowerBoundary, roundToStepUpperBoundary } from '@dh-io-emh/shared/src/utils/math.util';
import { FilterCode } from '@dh-io-emh/shared/src/models/product-filter/product-filter-base.model';

import {
  formatToMarketSpecificCurrency,
  SupportedMarket,
} from '@dh-io-emh/shared/src/utils/format-to-market-specific-currency.util';

const getOptionsDynamicStep = (min: number, max: number): number => {
  const differenceValue = max - min;
  const difference = roundToStepUpperBoundary(differenceValue, 1);

  if (difference < 15) {
    return 1;
  }

  const NUMBER_OF_FIRST_DIGITS = 2;
  const STEP_ORDER_NEGATIVE_POW_NUMBER = 1;

  const differenceString = String(difference);
  const differenceNumberOfDigits = differenceString.length;

  const firstTwoDigits = Number(differenceString.slice(0, NUMBER_OF_FIRST_DIGITS));
  const stepFirstDigits = roundToStep(firstTwoDigits, 10);
  const multiplierOrderNumber = differenceNumberOfDigits - NUMBER_OF_FIRST_DIGITS - STEP_ORDER_NEGATIVE_POW_NUMBER;
  const stepDigitsMultiplier = Math.pow(10, multiplierOrderNumber);

  return stepFirstDigits * stepDigitsMultiplier;
};

export const useStepRangeDropdownOptions = ({
  filter,
  step,
  emit,
  locale,
  formatCurrencyLabels = ref(false),
}: {
  filter: Ref<ProductFilterSlider> | ComputedRef<ProductFilterSlider>;
  step: Ref<number | undefined> | ComputedRef<number | undefined> | undefined;
  emit: (event: 'change', ...args: any[]) => void;
  locale?: Ref<SupportedMarket> | ComputedRef<SupportedMarket>;
  formatCurrencyLabels?: Ref<boolean>;
}) => {
  const dynamicStep = computed(() => {
    const { min, max } = filter.value;

    return step && step.value ? step.value : getOptionsDynamicStep(min, max);
  });

  const roundedFilterValues = computed(() => {
    const { min, max } = filter.value;
    const [selectedFrom, selectedTo] = filter.value.value;

    return {
      min: roundToStepLowerBoundary(min, dynamicStep.value),
      max: roundToStepUpperBoundary(max, dynamicStep.value),
      selectedFrom: roundToStepLowerBoundary(selectedFrom, dynamicStep.value),
      selectedTo: roundToStepUpperBoundary(selectedTo, dynamicStep.value),
    };
  });

  const mapRangeOption = ({
    optionValue,
    label,
    value,
    key,
    selected = false,
  }: { optionValue: number } & Partial<DropdownRangeOption>): DropdownRangeOption => {
    const formattedLabel =
      formatCurrencyLabels.value && locale?.value
        ? formatToMarketSpecificCurrency(optionValue, locale.value)
        : optionValue.toString();

    return {
      label: label ?? formattedLabel,
      value: value ?? optionValue.toString(),
      key: key ?? optionValue.toString(),
      selected,
    };
  };

  const minOptions = computed(() => {
    const startValue = roundedFilterValues.value.min;
    const endValue =
      filter.value.code === FilterCode.numberOfSeats
        ? roundedFilterValues.value.selectedTo + dynamicStep.value
        : roundedFilterValues.value.selectedTo;

    if (startValue === endValue) {
      return [mapRangeOption({ optionValue: roundedFilterValues.value.selectedFrom, selected: true })];
    }

    return range(startValue, endValue, dynamicStep.value).map(
      (optionValue): DropdownRangeOption =>
        mapRangeOption({ optionValue, selected: optionValue === roundedFilterValues.value.selectedFrom }),
    );
  });

  const maxOptions = computed(() => {
    const startValue =
      filter.value.code === FilterCode.numberOfSeats
        ? roundedFilterValues.value.selectedFrom
        : roundedFilterValues.value.selectedFrom + dynamicStep.value;

    // As lodash.range method doesn't include the end value we need to have it one step higher
    const endValue = roundedFilterValues.value.max + dynamicStep.value;

    if (startValue === endValue) {
      return [mapRangeOption({ optionValue: roundedFilterValues.value.selectedTo, selected: true })];
    }

    return range(startValue, endValue, dynamicStep.value).map(
      (optionValue): DropdownRangeOption =>
        mapRangeOption({ optionValue, selected: optionValue === roundedFilterValues.value.selectedTo }),
    );
  });

  const emitChangeFilter = (filterValue) =>
    emit('change', {
      filterCode: filter.value?.code,
      filterValue,
      filterType: 'dropdown',
      filterCurrentlySelectedItemValue: filterValue,
      selected: true,
    });

  const selectMinOption = ({ value }: DropdownRangeOption) => {
    const selectedNumericValue = parseFloat(value);

    if (selectedNumericValue === roundedFilterValues.value.selectedFrom) {
      return;
    }

    const filterValue = [selectedNumericValue, roundedFilterValues.value.selectedTo];

    emitChangeFilter(filterValue);
  };

  const selectMaxOption = ({ value }: DropdownRangeOption) => {
    const selectedNumericValue = parseFloat(value);

    if (selectedNumericValue === roundedFilterValues.value.selectedTo) {
      return;
    }

    const filterValue = [roundedFilterValues.value.selectedFrom, selectedNumericValue];

    emitChangeFilter(filterValue);
  };

  return {
    minOptions,
    maxOptions,
    selectMinOption,
    selectMaxOption,
  };
};
